import { Link, type ToOptions } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { XIcon } from "lucide-react";
import { useMemo } from "react";
import { Button } from "react-aria-components";
import { twMerge } from "tailwind-merge";

type Props = {
  children: React.ReactNode;
  className?: string;
  contentLayoutId?: string;
  animate?: boolean;
  backLinkOptions?: ToOptions;
  padding?: boolean;
  border?: boolean;
  responsivePadding?: boolean;
  onClickBack?: () => void;
};

export function SettingsContainer({
  children,
  className,
  animate = true,
  backLinkOptions,
  padding = true,
  border = true,
  responsivePadding = true,
  onClickBack,
}: Props) {
  const closeButtonClassName = useMemo(
    () =>
      "p-2 mt-3 mr-3 -translate-y-1/2 translate-x-1/2 text-theme-on-background cursor-pointer hover:opacity-90",
    [],
  );

  return (
    <div
      className={twMerge(
        "grow",
        border && "border-theme-outline-variant rounded-lg border",
        className,
      )}
    >
      <div
        className={twMerge(
          "size-full",
          padding && [
            "px-14 py-12",
            responsivePadding && "p-6 xl:px-14 xl:py-12",
          ],
        )}
      >
        <motion.div
          className={twMerge(
            "relative grid size-full *:col-start-1 *:row-start-1",
          )}
          {...(animate
            ? {
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                transition: { delay: 0.3 },
              }
            : null)}
        >
          <div className="flex flex-col items-end justify-start">
            {backLinkOptions ? (
              <Link {...backLinkOptions} className={closeButtonClassName}>
                <XIcon className="size-5" />
              </Link>
            ) : onClickBack ? (
              <Button className={closeButtonClassName} onPress={onClickBack}>
                <XIcon className="size-5" />
              </Button>
            ) : null}
          </div>

          <div className="flex size-full min-w-0 flex-col">{children}</div>
        </motion.div>
      </div>
    </div>
  );
}
